// Typed wrappers for the open /system-panel/* endpoints that power the info modal.
import { getJson, postJson, patchJson, del } from './restClient';

export type Exchange = 'binance' | 'mexc';

export interface SystemMetrics {
  system: {
    cpu_count_logical: number;
    cpu_count_physical: number | null;
    per_core_percent: number[];
    cpu_percent_avg: number;
    cores_busy: number;
    mem_total_bytes: number;
    mem_used_bytes: number;
    mem_available_bytes: number;
    mem_percent: number;
  };
  analysis_pool: {
    initialized: boolean;
    configured_max_workers: number;
    max_tasks_per_child: number;
    alive_workers: number;
    pids: number[];
    workers: { pid: number; cpu_percent: number; rss_bytes: number }[];
    cpu_percent_total: number;
    rss_bytes_total: number;
  };
  process: {
    pid: number;
    cpu_percent: number;
    rss_bytes: number;
    vms_bytes: number;
    num_threads: number;
    num_fds: number | null;
    asyncio_tasks: number;
    thread_pool_max_workers: number;
  };
  workers: {
    workers: number;
    running: number;
    pinned: number;
    subscribers: number;
    analyzers: number;
    fetchers: number;
    analysis_count: number;
  };
  services: {
    database: boolean;
    analysis_pool_initialized: boolean;
    mail_initialized: boolean;
    mail_health_running: boolean;
    event_loop_lag_ms: number;
    env: string;
  };
}

export interface SystemWorker {
  id: string;
  symbol: string;
  timeframe: string;
  use_futures: boolean;
  exchange: string;
  network: string;
  enabled: boolean;
  note: string | null;
  created_at: string;
  updated_at: string;
  key: string;
  live: boolean;
}

export interface LiveWorkerRow {
  kind: 'analyzer' | 'fetcher';
  key: string;
  symbol: string;
  timeframe: string;
  market: string;
  exchange: string;
  subscribers: number;
  running: boolean;
  pinned: boolean;
  [extra: string]: unknown;
}

export interface LiveWorkers {
  analyzers: LiveWorkerRow[];
  fetchers: LiveWorkerRow[];
  totals: {
    analyzers: number;
    fetchers: number;
    subscribers: number;
    running: number;
    pinned: number;
  };
}

export interface NewSystemWorker {
  symbol: string;
  timeframe: string;
  exchange: Exchange;
  use_futures: boolean;
  enabled?: boolean;
  note?: string | null;
}

export const fetchMetrics = (signal?: AbortSignal) =>
  getJson<SystemMetrics>('/system-panel/metrics', signal);

export const fetchLiveWorkers = (signal?: AbortSignal) =>
  getJson<LiveWorkers>('/system-panel/workers', signal);

export const fetchSystemWorkers = (signal?: AbortSignal) =>
  getJson<{ system_workers: SystemWorker[] }>('/system-panel/system-workers', signal).then(
    (r) => r.system_workers,
  );

export const createSystemWorker = (body: NewSystemWorker) =>
  postJson<SystemWorker>('/system-panel/system-workers', body);

export const updateSystemWorker = (id: string, body: { enabled?: boolean; note?: string }) =>
  patchJson<SystemWorker>(`/system-panel/system-workers/${id}`, body);

export const deleteSystemWorker = (id: string) =>
  del<{ status: string; id: string }>(`/system-panel/system-workers/${id}`);
